import { isEqual } from 'es-toolkit'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { Card, CardBody, Col, Row } from 'reactstrap'

import { getAllowedIPAddress, selectAllowedIPAddressStatus, upsertAllowedIPAddress } from 'slices/ipAllowListEditSlice'
import { showError, showSuccess } from 'slices/notificationSlice'
import { selectTenantsStatus } from 'slices/tenantsSlice'
import { ENABLE_DIALOG_ERROR_STATUS_CODES } from 'slices/utils'

import { NavMenu, CardSubmitFooter } from 'components/common'

import { FormInput } from './FormInput'

const MAX_ITEMS = 100

type Props = {
  isServiceManagement?: boolean
}

const IpAllowListEdit = ({ isServiceManagement: isTenantAdmin = false }: Props) => {
  const [ipAddresses, setIPAddresses] = useState<string[] | null>(null)
  const [ipAddressesValidity, setIPAddressesValidity] = useState(false)
  const [userIPAddress, setUserIPAddress] = useState<string>('')
  const { tenant } = useSelector(selectTenantsStatus, shallowEqual)
  const { allowedIPAddress, isRequesting, errorMessage } = useSelector(selectAllowedIPAddressStatus, shallowEqual)

  const [submitted, setSubmitted] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (tenant?.id && tenant.id !== 0) {
      dispatch(getAllowedIPAddress({ tenantId: tenant.id }))
    }
  }, [dispatch, tenant?.id])

  // allowedIPAddressから、フォームに値を設定する
  useEffect(() => {
    if (!allowedIPAddress) {
      return
    }
    setIPAddresses(allowedIPAddress.ipAddresses)
    setUserIPAddress(allowedIPAddress.userIPAddress)
  }, [allowedIPAddress])

  const disabled = useMemo(() => !ipAddressesValidity, [ipAddressesValidity])

  const unchanged = useMemo(
    () => isEqual(ipAddresses, allowedIPAddress?.ipAddresses),
    [ipAddresses, allowedIPAddress?.ipAddresses]
  )

  const handleCancel = useCallback(() => {
    setIPAddresses(allowedIPAddress?.ipAddresses || [])
  }, [allowedIPAddress?.ipAddresses])

  const handleSubmit = useCallback(() => {
    if (!tenant?.id) {
      return
    }
    dispatch(upsertAllowedIPAddress({ ipAddresses }, tenant.id))
    setSubmitted(true)
  }, [dispatch, ipAddresses, tenant?.id])

  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      dispatch(showSuccess())
    } else {
      if (!ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
        // ENABLE_DIALOGのときにはエラーダイアログが出るのでNotificationは出さない
        dispatch(showError())
      }
    }
    setSubmitted(false)
  }, [submitted, errorMessage, isRequesting, dispatch])

  const content = useMemo(() => {
    return (
      <div className="m-3">
        <Row className="mb-3 g-0">
          <Col className="font-x-large fw-bold">IPアドレス制限</Col>
        </Row>
        <Card>
          <CardBody>
            <Row>
              <Col md={8} className="overflow-auto">
                <div className="mb-3">
                  利用を許可するIPアドレスを入力してください。CIDR表記もできます。IPv6の記法には対応しておりません。
                  <br />
                  {`設定できるのは最大で${MAX_ITEMS}個までです。`}
                </div>
                <FormInput
                  items={ipAddresses}
                  maxItems={MAX_ITEMS}
                  onChange={setIPAddresses}
                  labelText="IPアドレス"
                  addButtonText="IPアドレスを追加"
                  onValidate={setIPAddressesValidity}
                />
                <div className="my-3 text-danger">
                  設定したIPアドレス以外からはアクセスできなくなります。固定IP環境でない場合は設定しないようにお気をつけください。
                </div>
                <div className="mb-3">現在アクセスしているIPアドレス: {userIPAddress}</div>
              </Col>
            </Row>
          </CardBody>
          <CardSubmitFooter
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            submitDisabled={disabled || unchanged}
            cancelDisabled={unchanged}
            updatedBy={allowedIPAddress?.updatedByName}
            updatedAt={allowedIPAddress?.updatedAt}
          />
        </Card>
      </div>
    )
  }, [
    ipAddresses,
    userIPAddress,
    disabled,
    unchanged,
    handleCancel,
    handleSubmit,
    allowedIPAddress?.updatedByName,
    allowedIPAddress?.updatedAt,
  ])

  return isTenantAdmin ? content : <NavMenu>{content}</NavMenu>
}

export default IpAllowListEdit
